import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectors,
  getIndividualReport,
  fetchAnalysis,
  fetchConcentrations,
  fetchPnLDistributions,
  fetchTurnover,
  fetchMarketRegimes,
  fetchRiskScatter,
  fetchBlotter,
  HISTORICAL_SCENARIO,
} from 'state';

import {
  useTrainerAIReportData,
  useTrainerBiasReportData,
} from '../../AI/aiReportShared';
import type { ScatterPoint } from '../components/RiskReturnScatter';
import { makeRelativizer } from '../helpers';
import { assembleStudioData, NEUTRAL_SESSION } from './buildLive';

/**
 * Per-player ("self") data source for the trainee Analysis Studio. Mirrors
 * useStudio2Data, but for a SINGLE player — the authenticated trainee's own
 * `state.app.player.item`. Every fetch here is scoped to the requester on the
 * server (see pm.api `players_in_scope`), so the cohort slices that land in
 * Redux contain ONLY this player's rows — client-side filtering is never the
 * boundary. There is NO leaderboard fetch (rank is dropped), NO group reports,
 * and NO per-roster loop.
 *
 * Returns the same `{ players, session, groupReports }` shape the studio shell
 * consumes, plus `scatterPoints` — the anonymised cohort risk/return cloud (the
 * one piece of cohort-shaped data a trainee sees, identity-stripped server-side
 * and suppressed below a minimum cohort size).
 */
export const useSelfStudioData = () => {
  const dispatch = useDispatch();
  const me = useSelector(selectors.player);
  const session = useSelector(selectors.activeSession);
  const analysisItems = useSelector(selectors.analysisItemsSelector);
  const ownId = me?.id ?? -1;
  const ownReport = useSelector(selectors.individualReport(ownId));
  const repCcy = useSelector(
    selectors.currencyById(session?.reporting_currency ?? -1),
  );
  const allOrders = useSelector(selectors.orders);
  const underlyingAssets = useSelector(selectors.underlyingAssets);
  const currencies = useSelector(selectors.currencies);
  const scatterRaw = useSelector(selectors.riskScatterPoints);

  // Historical sessions must never show a player real calendar dates (they could
  // identify the period). Build a "Day N since start" relativiser; in real-time
  // sessions the current date IS today, so no relativiser is needed.
  const isHistorical = session?.scenario_type === HISTORICAL_SCENARIO;
  const relativizeDate = useMemo(
    () => (isHistorical ? makeRelativizer(session?.start_date) : undefined),
    [isHistorical, session?.start_date],
  );

  // The trainee's `player.item` carries a live `metrics` array (grown via the
  // `metrics_received` socket) but a stale/empty `last_metrics` — only
  // retrieveSession ever sets `last_metrics`, and only if metrics existed then,
  // so the Snapshot/Risk KPIs read 0. Derive `last_metrics` from the metrics
  // tail (the same source the trainee Metrics tab uses). Self-only — the trainer
  // path keeps reading its real, server-provided `last_metrics`.
  const selfPlayer = useMemo(() => {
    if (!me?.id) return me;
    const tail = me.metrics?.length
      ? me.metrics[me.metrics.length - 1]
      : undefined;
    return tail ? { ...me, last_metrics: tail } : me;
  }, [me]);

  // AI + bias reports — the "Trainer"-named hooks are functionally generic
  // (dispatch + select); the backend scopes the payload to the trainee, so they
  // return only this player's report. The GROUP report hook is intentionally NOT
  // used here (group reports name peers).
  const sessionId = session?.id ?? 0;
  const { promptsObj, responses } = useTrainerAIReportData(sessionId);
  const { biasReports } = useTrainerBiasReportData(sessionId);

  // Session-level + cohort-scoped fetches — each returns ONLY the trainee's own
  // rows (server-enforced). NO fetchLeaderboard, NO group responses.
  useEffect(() => {
    if (session?.id == null) return;
    dispatch(fetchAnalysis({ session_id: session.id }));
    dispatch(fetchConcentrations({ session_id: session.id }));
    dispatch(fetchPnLDistributions({ session_id: session.id }));
    dispatch(fetchTurnover({ session_id: session.id }));
    dispatch(fetchMarketRegimes({ session_id: session.id }));
    dispatch(fetchRiskScatter({ session_id: session.id }));
    // The Decisions blotter reads the player's flat order bag (state.orders),
    // which the trainer populates via retrieve_player (trainer-only). For a
    // trainee that bag is loaded by fetch_blotter — normally only by the
    // Markets/Blotter/OpenOrders tabs, so the studio blotter was empty until the
    // player visited one of those. Load it here so the blotter populates on the
    // studio tab itself.
    dispatch(fetchBlotter({}));
  }, [dispatch, session?.id]);

  // The trainee's own individual report. The backend re-derives the player from
  // the authenticated user for trainees — the player_id sent here is never
  // trusted server-side, so it cannot be used to read a peer's report.
  useEffect(() => {
    if (session?.id != null && me?.id) {
      dispatch(getIndividualReport({ player_id: me.id }));
    }
  }, [dispatch, session?.id, me?.id]);

  // Anonymised cohort points → the scatter's normalised contract (raw fraction →
  // %, 2dp; the same scaling the trainer view applies to its named points).
  const scatterPoints: ScatterPoint[] = useMemo(
    () =>
      scatterRaw.map(pt => ({
        x: Number((pt.volatility * 100).toFixed(2)),
        y: Number((pt.portfolio_return * 100).toFixed(2)),
        isSelf: pt.is_self,
      })),
    [scatterRaw],
  );

  const data = useMemo(() => {
    // Real trainee/session not loaded yet → EMPTY payload (never mock) +
    // isReady:false; StudioShell renders the loading skeleton on !isReady.
    if (!session || !selfPlayer?.id) {
      return {
        players: [],
        session: NEUTRAL_SESSION,
        groupReports: [],
        isReady: false,
      };
    }
    return {
      ...assembleStudioData({
        roster: [selfPlayer],
        session,
        analysisItems,
        biasReports,
        promptsObj,
        responses,
        groupResponses: {},
        allOrders,
        underlyingAssets,
        currencies,
        focusedId: selfPlayer.id,
        focusedReport: ownReport,
        currencySymbol: repCcy?.symbol,
        leaderboard: [],
        relativizeDate,
        displaySurface: 'trainee',
      }),
      isReady: true,
    };
  }, [
    selfPlayer,
    session,
    analysisItems,
    biasReports,
    promptsObj,
    responses,
    allOrders,
    underlyingAssets,
    currencies,
    ownReport,
    repCcy,
    relativizeDate,
  ]);

  return { ...data, scatterPoints, relativizeDate };
};
